import type { AxiosInstance } from 'axios';
import CHART_STATUSES from '@/components/chartStatuses';
import type {
  Chart,
  ChartListQueryParams,
  ChartListResponse,
  ChartCreateRequest,
  ChartUpdateRequest,
  EntityId,
} from '@/types';

export const getCharts = async (
  api: AxiosInstance,
  document_type_id: EntityId,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<ChartListResponse> => {
  const resp = await api.get<ChartListResponse>('/chart', {
    params: { document_type_id, limit, start_key, name } as ChartListQueryParams,
  });
  return resp.data;
};

export const getActiveCharts = async (
  api: AxiosInstance,
  document_type_id: EntityId,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<ChartListResponse> => {
  const resp = await api.get<ChartListResponse>('/chart/active', {
    params: { document_type_id, limit, start_key, name } as ChartListQueryParams,
  });
  return resp.data;
};

export const getPendingCharts = async (
  api: AxiosInstance,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<ChartListResponse> => {
  const resp = await api.get<ChartListResponse>('/chart/pending', {
    params: { limit, start_key, name } as ChartListQueryParams,
  });
  return resp.data;
};

export const getChart = async (
  api: AxiosInstance,
  chart_id: EntityId,
): Promise<Chart> => {
  const resp = await api.get<Chart>(`/chart/${chart_id}`);
  return resp.data;
};

export const createChart = async (
  api: AxiosInstance,
  values: ChartCreateRequest,
): Promise<Chart> => {
  const resp = await api.post<Chart>(`/chart/`, values);
  return resp.data;
};

export const updateChart = async (
  api: AxiosInstance,
  chart_id: EntityId,
  values: ChartUpdateRequest,
): Promise<Chart> => {
  const resp = await api.put<Chart>(`/chart/${chart_id}`, values);
  return resp.data;
};

export const deleteChart = async (
  api: AxiosInstance,
  chart_id: EntityId,
): Promise<void> => {
  const resp = await api.delete(`/chart/${chart_id}`);
  return resp.data;
};

export const generateChartElements = async (
  api: AxiosInstance,
  chart_id: EntityId,
): Promise<unknown> => {
  const resp = await api.post(`/chart/${chart_id}/generate`, {});
  return resp.data;
};

export const submitChart = async (
  api: AxiosInstance,
  chart_id: EntityId,
): Promise<Chart> => {
  return await updateChart(api, chart_id, {
    status: CHART_STATUSES.PENDING,
  });
};

export const approveChart = async (
  api: AxiosInstance,
  chart_id: EntityId,
): Promise<Chart> => {
  return await updateChart(api, chart_id, {
    status: CHART_STATUSES.ACTIVE,
  });
};

export const rejectChart = async (
  api: AxiosInstance,
  chart_id: EntityId,
): Promise<Chart> => {
  const resp = await api.put<Chart>(`/chart/${chart_id}`, {
    status: CHART_STATUSES.DRAFT,
  });
  return resp.data;
};

